import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client"
import type { ComponentProps } from "react"

/**
 * Normalizes a server domain URL for API consumption
 * @param domain - The base domain URL
 * @param platform - Optional platform identifier (e.g., 'android', 'ios')
 * @returns Normalized domain URL ending with a slash
 *
 * @example
 * normalizeDomain('http://localhost:3000', 'android')
 * // Returns: 'http://10.0.2.2:3000/'
 *
 * @example
 * normalizeDomain('https://api.example.com', 'ios')
 * // Returns: 'https://api.example.com/'
 */
export const normalizeDomain = (domain: string, platform?: string): string => {
  let normalizedDomain = domain

  // Handle Android emulator localhost URL
  // Android emulator uses 10.0.2.2 to access host machine's localhost
  if (platform === "android" && normalizedDomain.includes("localhost")) {
    normalizedDomain = normalizedDomain.replace("localhost", "10.0.2.2")
  }

  // Ensure domain ends with a slash for proper URL construction
  return normalizedDomain.endsWith("/")
    ? normalizedDomain
    : `${normalizedDomain}/`
}

// Resource namespaces (queryKey[0]) that must never be written to disk:
// endpoints that contact the Stripe API, plus security/audit trails (IPs,
// device fingerprints, cross-resource snapshots).
const SENSITIVE_QUERY_NAMESPACES = new Set<unknown>([
  // Security & audit trails
  "security-events",
  "audit-logs",
  "dead-letter-jobs",
  // Stripe-backed resources
  "payment",
  "payment-intents",
  "payment-methods",
  "setup-intents",
  "payouts",
  "stripe",
])

export const shouldDehydrateQuery: NonNullable<
  NonNullable<
    ComponentProps<typeof PersistQueryClientProvider>["persistOptions"]
  >["dehydrateOptions"]
>["shouldDehydrateQuery"] = (query) =>
  query.state.status === "success" &&
  !SENSITIVE_QUERY_NAMESPACES.has(query.queryKey[0])
